import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Users, BookOpen, School, Church, GraduationCap, 
  Loader2, RefreshCw, UserPlus, Trash2, Key, Edit2, Save, X,
  Building, BarChart3, FileText, Calendar, Mail, Phone, Check, AlertCircle,
  Upload, Download
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import PasswordStrengthMeter, { isPasswordValid } from '@/components/PasswordStrengthMeter';

const OrganizationDetailView = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState(null);
  const [users, setUsers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [reports, setReports] = useState(null);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Dialog states
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [resetPwdDialog, setResetPwdDialog] = useState({ open: false, userId: null, userName: '' });
  const [editOrgDialog, setEditOrgDialog] = useState(false);
  const [bulkImportDialog, setBulkImportDialog] = useState(false);
  
  // Form states
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '', account_type: 'teacher' });
  const [resetPassword, setResetPassword] = useState('');
  const [editOrgData, setEditOrgData] = useState({});
  
  // Bulk import states
  const [csvData, setCsvData] = useState([]);
  const [importResults, setImportResults] = useState(null);
  const [importing, setImporting] = useState(false);
  
  // Loading states
  const [addingUser, setAddingUser] = useState(false);
  const [resettingPwd, setResettingPwd] = useState(false);
  const [savingOrg, setSavingOrg] = useState(false);

  useEffect(() => {
    fetchOrganizationData();
    fetchReports();
  }, [orgId]);

  const fetchOrganizationData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/organizations/${orgId}`);
      setOrganization(response.data.organization);
      setUsers(response.data.users || []);
      setClasses(response.data.classes || []);
      setStudents(response.data.students || []);
      setStats(response.data.stats || {});
      setEditOrgData({
        name: response.data.organization?.name || '',
        contact_email: response.data.organization?.contact_email || '',
        contact_phone: response.data.organization?.contact_phone || '',
        student_licenses: response.data.organization?.student_licenses || 50
      });
    } catch (error) {
      console.error('Failed to fetch organization:', error);
      toast.error('Failed to load organization details');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await api.get(`/admin/organization/${orgId}/reports`);
      setReports(response.data);
    } catch (error) {
      console.log('Reports endpoint not available yet');
      // Generate mock reports for now
      setReports({
        activity_summary: {
          total_activities_completed: 256,
          average_score: 78,
          most_active_subject: 'Math'
        },
        engagement: {
          daily_active_students: 12,
          weekly_active_students: 35,
          monthly_active_students: 48
        },
        performance_by_subject: [
          { subject: 'Math', avgScore: 82, completed: 89 },
          { subject: 'Reading', avgScore: 75, completed: 76 },
          { subject: 'Science', avgScore: 80, completed: 54 },
          { subject: 'Writing', avgScore: 72, completed: 37 }
        ],
        weekly_trend: [
          { week: 'W1', activities: 45, students: 28 },
          { week: 'W2', activities: 52, students: 32 },
          { week: 'W3', activities: 48, students: 30 },
          { week: 'W4', activities: 61, students: 38 }
        ]
      });
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.name || !newUser.password) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setAddingUser(true);
    try {
      await api.post(`/admin/organizations/${orgId}/users`, newUser);
      toast.success('User added successfully');
      setAddUserDialog(false);
      setNewUser({ email: '', name: '', password: '', account_type: 'teacher' });
      fetchOrganizationData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleResetPassword = async () => {
    if (!isPasswordValid(resetPassword || '')) {
      toast.error('Password must include upper, lower, number, and special character (min 8).');
      return;
    }
    
    setResettingPwd(true);
    try {
      await api.post(`/admin/organizations/${orgId}/reset-password/${resetPwdDialog.userId}?new_password=${encodeURIComponent(resetPassword)}`);
      toast.success('Password reset successfully');
      setResetPwdDialog({ open: false, userId: null, userName: '' });
      setResetPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPwd(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm('Are you sure you want to remove this user from the organization?')) return;
    
    try {
      await api.delete(`/admin/organizations/${orgId}/users/${userId}`);
      toast.success('User removed from organization');
      fetchOrganizationData();
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await api.put(`/admin/organizations/${orgId}/users/${userId}/role?new_role=${newRole}`);
      toast.success('User role updated');
      fetchOrganizationData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleUpdateOrganization = async () => {
    setSavingOrg(true);
    try {
      await api.put(`/admin/organizations/${orgId}`, editOrgData);
      toast.success('Organization updated successfully');
      setEditOrgDialog(false);
      fetchOrganizationData();
    } catch (error) {
      toast.error('Failed to update organization');
    } finally {
      setSavingOrg(false);
    }
  };

  // ==================== BULK IMPORT FUNCTIONS ====================
  
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
        
        const students = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          if (values.length >= 1 && values[0]) {
            const student = {};
            headers.forEach((header, idx) => {
              if (header === 'name' || header === 'student name' || header === 'student_name') {
                student.name = values[idx];
              } else if (header === 'birth_date' || header === 'birthdate' || header === 'dob' || header === 'date of birth') {
                student.birth_date = values[idx];
              } else if (header === 'grade' || header === 'grade_level' || header === 'grade level') {
                student.grade_level = values[idx];
              } else if (header === 'class' || header === 'class_name' || header === 'classroom') {
                student.class_name = values[idx];
              }
            });
            if (student.name) {
              students.push(student);
            }
          }
        }
        
        setCsvData(students);
        toast.success(`Parsed ${students.length} students from CSV`);
      } catch (error) {
        toast.error('Failed to parse CSV file');
        console.error(error);
      }
    };
    reader.readAsText(file);
  };
  
  const handleBulkImport = async () => {
    if (csvData.length === 0) {
      toast.error('No students to import');
      return;
    }
    
    setImporting(true);
    try {
      const response = await api.post(`/admin/organizations/${orgId}/import-students`, {
        students: csvData
      });
      setImportResults(response.data);
      toast.success(`Imported ${response.data.imported_count} students`);
      if (response.data.error_count > 0) {
        toast.warning(`${response.data.error_count} students failed to import`);
      }
      fetchOrganizationData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import students');
    } finally {
      setImporting(false);
    }
  };
  
  const downloadCsvTemplate = () => {
    const template = 'name,birth_date,grade_level,class_name\nJohn Smith,2018-05-15,K,Kindergarten A\nJane Doe,2017-08-20,1,First Grade\n';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'student_import_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const closeBulkImportDialog = () => {
    setBulkImportDialog(false);
    setCsvData([]);
    setImportResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444'];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  const OrgIcon = organization?.organization_type === 'church' || organization?.organization_type === 'sunday_school'
    ? Church
    : School;

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="icon" data-testid="back-to-admin-btn">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                  organization?.organization_type === 'church' || organization?.organization_type === 'sunday_school'
                    ? 'bg-purple-100'
                    : 'bg-emerald-100'
                }`}>
                  <OrgIcon className={`w-6 h-6 ${
                    organization?.organization_type === 'church' || organization?.organization_type === 'sunday_school'
                      ? 'text-purple-600'
                      : 'text-emerald-600'
                  }`} />
                </div>
                <div>
                  <h1 className="font-bold text-xl text-slate-800" data-testid="org-name-header">
                    {organization?.name}
                  </h1>
                  <p className="text-sm text-slate-500 capitalize">
                    {organization?.organization_type?.replace('_', ' ')}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={fetchOrganizationData} data-testid="refresh-org-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Refresh
              </Button>
              <Button variant="outline" onClick={() => setEditOrgDialog(true)} data-testid="edit-org-btn">
                <Edit2 className="w-4 h-4 mr-2" /> Edit
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => setActiveTab('users')} data-testid="stat-teachers">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Teachers</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.teachers || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => setActiveTab('students')} data-testid="stat-students">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <GraduationCap className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Students</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.students || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="cursor-pointer hover:shadow-md transition-all" onClick={() => setActiveTab('classes')} data-testid="stat-classes">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Classes</p>
                  <p className="text-2xl font-bold text-slate-900">{stats?.classes || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card data-testid="stat-licenses">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                  <Key className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Licenses</p>
                  <p className={`text-2xl font-bold ${
                    (stats?.licenses_used || 0) > (stats?.licenses_total || 0)
                      ? 'text-red-600'
                      : 'text-slate-900'
                  }`}>
                    {stats?.licenses_used || 0}/{stats?.licenses_total || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white border">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
            <TabsTrigger value="classes" data-testid="tab-classes">Classes</TabsTrigger>
            <TabsTrigger value="students" data-testid="tab-students">Students</TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Organization Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Building className="w-5 h-5 text-slate-600" />
                    Organization Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-500">Type</p>
                      <p className="font-medium capitalize">{organization?.organization_type?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Status</p>
                      <div className="flex items-center gap-2">
                        {organization?.onboarding_completed ? (
                          <>
                            <Check className="w-4 h-4 text-green-600" />
                            <span className="text-green-600 font-medium">Active</span>
                          </>
                        ) : (
                          <>
                            <AlertCircle className="w-4 h-4 text-amber-600" />
                            <span className="text-amber-600 font-medium">Onboarding Pending</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Mail className="w-3 h-3" /> Contact Email
                      </p>
                      <p className="font-medium">{organization?.contact_email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> Phone
                      </p>
                      <p className="font-medium">{organization?.contact_phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> Created
                      </p>
                      <p className="font-medium">
                        {organization?.created_at ? new Date(organization.created_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Subscription</p>
                      <p className="font-medium capitalize">{organization?.subscription_plan || 'enterprise'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Stats Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-slate-600" />
                    User Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Teachers', value: stats?.teachers || 0 },
                            { name: 'Org Admins', value: stats?.org_admins || 0 },
                            { name: 'Students', value: stats?.students || 0 }
                          ]}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                        >
                          {[0, 1, 2].map((index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Organization Users ({users.length})</CardTitle>
                  <Button onClick={() => setAddUserDialog(true)} data-testid="add-user-btn">
                    <UserPlus className="w-4 h-4 mr-2" /> Add User
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-8 text-slate-500">
                          No users in this organization
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => (
                        <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                          <TableCell className="font-medium">{user.name}</TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <Select
                              value={user.account_type}
                              onValueChange={(value) => handleUpdateUserRole(user.id, value)}
                            >
                              <SelectTrigger className="w-32" data-testid={`role-select-${user.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="teacher">Teacher</SelectItem>
                                <SelectItem value="org_admin">Org Admin</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-slate-500">
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setResetPwdDialog({ open: true, userId: user.id, userName: user.name })}
                                data-testid={`reset-pwd-${user.id}`}
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleRemoveUser(user.id)}
                                data-testid={`remove-user-${user.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Classes Tab */}
          <TabsContent value="classes">
            <Card>
              <CardHeader>
                <CardTitle>Classes ({classes.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Class Name</TableHead>
                      <TableHead>Grade Level</TableHead>
                      <TableHead>Subject Focus</TableHead>
                      <TableHead>Students</TableHead>
                      <TableHead>Max Students</TableHead>
                      <TableHead>Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classes.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          No classes in this organization
                        </TableCell>
                      </TableRow>
                    ) : (
                      classes.map((cls) => (
                        <TableRow key={cls.id} data-testid={`class-row-${cls.id}`}>
                          <TableCell className="font-medium">{cls.name}</TableCell>
                          <TableCell>{cls.grade_level || 'N/A'}</TableCell>
                          <TableCell className="capitalize">{cls.subject_focus || 'General'}</TableCell>
                          <TableCell>{cls.student_ids?.length || 0}</TableCell>
                          <TableCell>{cls.max_students || 30}</TableCell>
                          <TableCell className="text-slate-500">
                            {cls.created_at ? new Date(cls.created_at).toLocaleDateString() : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Students ({students.length})</CardTitle>
                  <Button onClick={() => setBulkImportDialog(true)} data-testid="bulk-import-btn">
                    <Upload className="w-4 h-4 mr-2" /> Bulk Import
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Age Group</TableHead>
                      <TableHead>Grade Level</TableHead>
                      <TableHead>Current Level</TableHead>
                      <TableHead>Total XP</TableHead>
                      <TableHead>Daily Streak</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {students.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          No students in this organization. Use "Bulk Import" to add students from CSV.
                        </TableCell>
                      </TableRow>
                    ) : (
                      students.map((student) => (
                        <TableRow key={student.id} data-testid={`student-row-${student.id}`}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600 font-bold text-sm">
                                {student.name?.charAt(0).toUpperCase()}
                              </div>
                              {student.name}
                            </div>
                          </TableCell>
                          <TableCell className="capitalize">{student.age_group?.replace('_', ' ') || 'N/A'}</TableCell>
                          <TableCell>{student.grade_level || 'N/A'}</TableCell>
                          <TableCell>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              Level {student.current_level || 1}
                            </span>
                          </TableCell>
                          <TableCell>{student.total_xp || 0} XP</TableCell>
                          <TableCell>
                            <span className={`${student.daily_streak > 0 ? 'text-orange-600 font-medium' : 'text-slate-500'}`}>
                              {student.daily_streak || 0} days
                            </span>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Activity Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-600" />
                    Activity Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Total Activities</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {reports?.activity_summary?.total_activities_completed || 0}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Average Score</p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {reports?.activity_summary?.average_score || 0}%
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg col-span-2">
                      <p className="text-sm text-slate-500">Most Active Subject</p>
                      <p className="text-xl font-bold text-slate-900">
                        {reports?.activity_summary?.most_active_subject || 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Engagement Stats */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-slate-600" />
                    Student Engagement
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">
                        {reports?.engagement?.daily_active_students || 0}
                      </p>
                      <p className="text-xs text-slate-500">Daily Active</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">
                        {reports?.engagement?.weekly_active_students || 0}
                      </p>
                      <p className="text-xs text-slate-500">Weekly Active</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">
                        {reports?.engagement?.monthly_active_students || 0}
                      </p>
                      <p className="text-xs text-slate-500">Monthly Active</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Performance by Subject */}
              <Card>
                <CardHeader>
                  <CardTitle>Performance by Subject</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                      <BarChart data={reports?.performance_by_subject || []}>
                        <XAxis dataKey="subject" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="avgScore" fill="#10b981" name="Avg Score" />
                        <Bar dataKey="completed" fill="#3b82f6" name="Completed" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Weekly Trend */}
              <Card>
                <CardHeader>
                  <CardTitle>Weekly Activity Trend</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
                      <LineChart data={reports?.weekly_trend || []}>
                        <XAxis dataKey="week" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="activities" stroke="#10b981" strokeWidth={2} name="Activities" />
                        <Line type="monotone" dataKey="students" stroke="#3b82f6" strokeWidth={2} name="Students" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add User Dialog */}
      <Dialog open={addUserDialog} onOpenChange={setAddUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              Add User to Organization
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                placeholder="John Smith"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="mt-1"
                data-testid="new-user-name"
              />
            </div>
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                placeholder="user@school.edu"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="mt-1"
                data-testid="new-user-email"
              />
            </div>
            <div>
              <Label>Password *</Label>
              <Input
                type="password"
                placeholder="Minimum 6 characters"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="mt-1"
                data-testid="new-user-password"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={newUser.account_type} onValueChange={(v) => setNewUser({ ...newUser, account_type: v })}>
                <SelectTrigger className="mt-1" data-testid="new-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="org_admin">Organization Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddUserDialog(false)}>Cancel</Button>
            <Button onClick={handleAddUser} disabled={addingUser} data-testid="submit-add-user">
              {addingUser && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPwdDialog.open} onOpenChange={(open) => !open && setResetPwdDialog({ open: false, userId: null, userName: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-amber-600" />
              Reset Password for {resetPwdDialog.userName}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label>New Password</Label>
            <Input
              type="password"
              placeholder="Create a strong password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              className="mt-1"
              data-testid="reset-password-input"
            />
            <div className="mt-2">
              <PasswordStrengthMeter password={resetPassword} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPwdDialog({ open: false, userId: null, userName: '' })}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} disabled={resettingPwd} data-testid="submit-reset-pwd">
              {resettingPwd && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={editOrgDialog} onOpenChange={setEditOrgDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-blue-600" />
              Edit Organization
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Organization Name</Label>
              <Input
                value={editOrgData.name}
                onChange={(e) => setEditOrgData({ ...editOrgData, name: e.target.value })}
                className="mt-1"
                data-testid="edit-org-name"
              />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input
                type="email"
                value={editOrgData.contact_email}
                onChange={(e) => setEditOrgData({ ...editOrgData, contact_email: e.target.value })}
                className="mt-1"
                data-testid="edit-org-email"
              />
            </div>
            <div>
              <Label>Contact Phone</Label>
              <Input
                value={editOrgData.contact_phone || ''}
                onChange={(e) => setEditOrgData({ ...editOrgData, contact_phone: e.target.value })}
                className="mt-1"
                data-testid="edit-org-phone"
              />
            </div>
            <div>
              <Label>Student Licenses</Label>
              <Input
                type="number"
                value={editOrgData.student_licenses}
                onChange={(e) => setEditOrgData({ ...editOrgData, student_licenses: parseInt(e.target.value) || 0 })}
                className="mt-1"
                data-testid="edit-org-licenses"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOrgDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateOrganization} disabled={savingOrg} data-testid="submit-edit-org">
              {savingOrg && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Bulk Import Dialog */}
      <Dialog open={bulkImportDialog} onOpenChange={(open) => !open && closeBulkImportDialog()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-600" />
              Bulk Import Students
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <h4 className="font-medium mb-2">CSV Format Requirements:</h4>
              <ul className="text-sm text-slate-600 space-y-1">
                <li>• <strong>name</strong> (required) - Student's full name</li>
                <li>• <strong>birth_date</strong> (optional) - Format: YYYY-MM-DD</li>
                <li>• <strong>grade_level</strong> (optional) - e.g., K, 1, 2, 3</li>
                <li>• <strong>class_name</strong> (optional) - Assign to existing class</li>
              </ul>
              <Button variant="link" size="sm" onClick={downloadCsvTemplate} className="mt-2 p-0 h-auto" data-testid="download-template-btn">
                <Download className="w-4 h-4 mr-1" /> Download Template
              </Button>
            </div>
            
            <div>
              <Label>Upload CSV File</Label>
              <Input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="mt-1"
                data-testid="csv-file-input"
              />
            </div>
            
            {csvData.length > 0 && (
              <div>
                <Label className="mb-2 block">Preview ({csvData.length} students)</Label>
                <div className="max-h-48 overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Birth Date</TableHead>
                        <TableHead>Grade</TableHead>
                        <TableHead>Class</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {csvData.slice(0, 10).map((student, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="py-2">{student.name}</TableCell>
                          <TableCell className="py-2">{student.birth_date || '-'}</TableCell>
                          <TableCell className="py-2">{student.grade_level || '-'}</TableCell>
                          <TableCell className="py-2">{student.class_name || '-'}</TableCell>
                        </TableRow>
                      ))}
                      {csvData.length > 10 && (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-sm text-slate-500 py-2">
                            ... and {csvData.length - 10} more
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
            
            {importResults && (
              <div className={`p-4 rounded-lg ${importResults.error_count > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                <h4 className="font-medium mb-2">Import Results:</h4>
                <div className="flex gap-6">
                  <div>
                    <span className="text-green-600 font-bold text-lg">{importResults.imported_count}</span>
                    <p className="text-xs text-slate-500">Imported</p>
                  </div>
                  <div>
                    <span className="text-red-600 font-bold text-lg">{importResults.error_count}</span>
                    <p className="text-xs text-slate-500">Errors</p>
                  </div>
                </div>
                {importResults.errors?.length > 0 && (
                  <div className="mt-2 text-sm text-red-600">
                    <p className="font-medium">Errors:</p>
                    {importResults.errors.slice(0, 5).map((err, idx) => (
                      <p key={idx}>Row {err.row}: {err.error}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <div className="bg-amber-50 p-3 rounded-lg flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Available licenses: {(stats?.licenses_total || 0) - (stats?.licenses_used || 0)}. 
                Import will fail if there are not enough licenses.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeBulkImportDialog}>Cancel</Button>
            <Button 
              onClick={handleBulkImport} 
              disabled={importing || csvData.length === 0}
              data-testid="submit-bulk-import"
            >
              {importing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Import {csvData.length} Students
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationDetailView;
